import type { TakeoverRequest, TakeoverResponse } from '@/composables/Device/DeviceTakeover'

/**
 * Why a decline response was sent (FLY3D-1023).
 *
 * - `user`: the current owner pressed Decline (or closed the modal).
 * - `no_response`: the owner's app was open but nobody answered before the
 *   modal countdown ran out, so the client declined on their behalf.
 * - `flying`: the owner's aircraft was in the air, so the client declined
 *   automatically for safety without ever showing the modal.
 *
 * Only `user` is an explicit refusal. The requester must never be told the
 * owner declined when the owner did not actually make that choice.
 */
export type TakeoverDeclineReason = 'user' | 'no_response' | 'flying'

/**
 * Takeover timing (FLY3D-1023). One source of truth so the owner's countdown,
 * the requester's fallback, and the text shown to the requester cannot drift.
 *
 * Owner countdown: 30s. Raised from 10s to match what the requester is told.
 * It restarts on window focus (FLY3D-866), so `OWNER_COUNTDOWN_BACKSTOP_MS`
 * caps the total time an ignored modal can stay open.
 *
 * Requester fallback: a little longer than the owner countdown so the owner's
 * `no_response` reply normally lands first and the requester is not shown
 * their own timeout prompt only to have it replaced a moment later.
 */
export const OWNER_COUNTDOWN_SECONDS = 30
export const OWNER_COUNTDOWN_BACKSTOP_MS = 90_000
export const REQUESTER_RESPONSE_TIMEOUT_MS = 40_000

export interface TakeoverDeclineOutcome {
  title: string
  message: string
  notificationTitle: string
  notificationBody: string
  /** True only when the owner made the decision themselves. */
  explicit: boolean
}

/**
 * Resolves the decline reason from a response payload.
 *
 * Older clients send no `reason`; they only set `isFlying`. Treat those as
 * an explicit decline when not flying so behaviour with mixed versions
 * matches what it was before the field existed.
 */
export const resolveDeclineReason = (
  response: Pick<TakeoverResponse, 'reason' | 'isFlying'>,
): TakeoverDeclineReason => {
  if (response.reason) return response.reason
  return response.isFlying ? 'flying' : 'user'
}

const typeWord = (request: Pick<TakeoverRequest, 'type'>) => request.type.toLowerCase()

/**
 * Builds the text the requester sees when a decline response arrives.
 * Pure so it can be unit tested without stores or MQTT.
 */
export const describeDeclineOutcome = (
  response: Pick<TakeoverResponse, 'reason' | 'isFlying'> & {
    request: Pick<TakeoverRequest, 'type' | 'deviceGroupName'>
  },
): TakeoverDeclineOutcome => {
  const { request } = response
  const reason = resolveDeclineReason(response)
  const group = `"${request.deviceGroupName}"`

  if (reason === 'no_response') {
    const title = 'No Response Received'
    return {
      title,
      message: `The user did not respond to your ${typeWord(request)} request for the ${group} device group within the time limit, so it was declined automatically.`,
      notificationTitle: title,
      notificationBody: `No response to your ${typeWord(request)} request for ${group}. It was declined automatically.`,
      explicit: false,
    }
  }

  if (reason === 'flying') {
    const title = `${request.type} Request Declined`
    return {
      title,
      message: `The user declined your request to ${typeWord(request)} the ${group} device group because the drone is currently flying. For safety reasons, device groups cannot be transferred during active flight operations. Please wait until the flight is complete before requesting ${typeWord(request)} again.`,
      notificationTitle: title,
      notificationBody: `Your ${typeWord(request)} request for ${group} was declined because the drone is flying.`,
      explicit: false,
    }
  }

  const title = `${request.type} Request Declined`
  const message =
    request.type === 'Handover'
      ? `The user declined your request to handover the ${group} to them. They cannot be assigned to this group unless an administrator overrides this decision.`
      : `The user declined your request to take over the ${group} device group. You cannot be assigned to this group unless an administrator overrides this decision.`
  return {
    title,
    message,
    notificationTitle: title,
    notificationBody: `Your ${typeWord(request)} request for ${group} was declined.`,
    explicit: true,
  }
}

/**
 * Text shown to the current owner after their own client auto-declined a
 * request they never answered, so they know why the modal disappeared.
 */
export const describeAutoDeclineForOwner = (
  request: Pick<TakeoverRequest, 'type' | 'deviceGroupName' | 'requestingUserName' | 'initiator'>,
): { title: string; message: string } => {
  const requester = request.type === 'Handover' ? request.initiator : request.requestingUserName
  return {
    title: `${request.type} Request Declined Automatically`,
    message: `You did not respond to ${requester}'s ${typeWord(request)} request for "${request.deviceGroupName}" in time, so it was declined automatically.`,
  }
}
